import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  FileText, 
  Layers, 
  Calculator, 
  BarChart3, 
  Settings, 
  Bell, 
  Search,
  LogOut,
  ChevronDown,
  Plus,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Users,
  Menu,
  X,
  CreditCard,
  User
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

// Mock data for dashboard
const mockStats = [
  { label: 'Active RFQs', value: '24', change: '+3', trend: 'up', icon: FileText },
  { label: 'Pending Quotes', value: '12', change: '-2', trend: 'down', icon: Clock },
  { label: 'Win Rate', value: '68%', change: '+5%', trend: 'up', icon: TrendingUp },
  { label: 'Avg. Cycle Time', value: '3.2d', change: '-0.8d', trend: 'up', icon: BarChart3 },
];

const mockRFQs = [
  { id: 'RFQ-2024-001', customer: 'Mercedes-Benz', status: 'Technical Evaluation', dueDate: '2024-12-20', value: '$125,000' },
  { id: 'RFQ-2024-002', customer: 'BMW Group', status: 'Costing', dueDate: '2024-12-22', value: '$89,500' },
  { id: 'RFQ-2024-003', customer: 'Volkswagen', status: 'CFT Approval', dueDate: '2024-12-18', value: '$210,000' },
  { id: 'RFQ-2024-004', customer: 'Audi', status: 'Quote Preparation', dueDate: '2024-12-25', value: '$156,000' },
];

const sidebarItems = [
  { name: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
  { name: 'RFQs', icon: FileText, href: '/dashboard/rfqs' },
  { name: 'BOMs', icon: Layers, href: '/dashboard/boms' },
  { name: 'Costing', icon: Calculator, href: '/dashboard/costing' },
  { name: 'Analytics', icon: BarChart3, href: '/dashboard/analytics' },
  { name: 'Team', icon: Users, href: '/dashboard/team' },
];

const bottomItems = [
  { name: 'Billing', icon: CreditCard, href: '/billing' },
  { name: 'Profile', icon: User, href: '/profile' },
  { name: 'Settings', icon: Settings, href: '/dashboard/settings' },
];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(30);

  useEffect(() => {
    // Check auth state
    const userData = localStorage.getItem('costgenie_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Calculate trial days left
      if (parsedUser.trialEndsAt) {
        const daysLeft = Math.ceil((new Date(parsedUser.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
        setTrialDaysLeft(Math.max(0, daysLeft));
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('costgenie_user');
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#030810] flex" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside 
        className={`dashboard-sidebar fixed lg:static inset-y-0 left-0 z-50 transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-300`}
        data-testid="dashboard-sidebar"
      >
        <div className="h-full flex flex-col p-4">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8 px-2">
            <Link to="/">
              <img src={LOGO_URL} alt="CostGenie" className="h-8 w-auto" />
            </Link>
            <button 
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-1 text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Trial Banner */}
          {user.plan === 'Trial' && (
            <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-cyan-500/10 to-transparent border border-cyan-500/20">
              <p className="text-cyan-400 text-xs font-medium mb-1">Free Trial</p>
              <p className="text-white/60 text-xs">{trialDaysLeft} days remaining</p>
              <Link 
                to="/billing"
                className="mt-2 inline-block text-xs text-cyan-400 hover:text-cyan-300"
              >
                Upgrade now →
              </Link>
            </div>
          )}

          {/* Main Nav */}
          <nav className="flex-1 space-y-1">
            {sidebarItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-item ${location.pathname === item.href ? 'active' : ''}`}
                data-testid={`sidebar-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Bottom Nav */}
          <div className="border-t border-white/5 pt-4 mt-4 space-y-1">
            {bottomItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-item ${location.pathname === item.href ? 'active' : ''}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.name}</span>
              </Link>
            ))}
            <button 
              onClick={handleLogout}
              className="sidebar-item w-full text-red-400/80 hover:text-red-400 hover:bg-red-500/5"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="h-16 border-b border-white/5 flex items-center justify-between px-4 lg:px-8" data-testid="dashboard-header">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 text-white/60 hover:text-white"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search RFQs, BOMs, quotes..."
                className="w-64 lg:w-80 pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
                data-testid="dashboard-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 text-white/60 hover:text-white transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-cyan-500" />
            </button>
            <div className="flex items-center gap-3 pl-4 border-l border-white/10">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm text-white/90">{user.name}</p>
                <p className="text-xs text-white/40">{user.plan} Plan</p>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Welcome back, {user.name?.split(' ')[0]}
            </h1>
            <p className="text-white/40 text-sm">Here's what's happening with your RFQs today.</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {mockStats.map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-panel p-5"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className={`text-xs font-medium ${stat.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                    {stat.change}
                  </span>
                </div>
                <p className="text-2xl font-semibold text-white mb-1">{stat.value}</p>
                <p className="text-white/40 text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button className="btn-premium btn-premium-primary" data-testid="new-rfq-btn">
              <Plus className="w-4 h-4" />
              <span className="relative z-10">New RFQ</span>
            </button>
            <button className="btn-premium btn-premium-outline">
              <Layers className="w-4 h-4" />
              <span>Create BOM</span>
            </button>
            <button className="btn-premium btn-premium-outline">
              <Calculator className="w-4 h-4" />
              <span>Start Costing</span>
            </button>
          </div>

          {/* Recent RFQs */}
          <div className="glass-panel overflow-hidden" data-testid="rfq-table">
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-semibold">Recent RFQs</h2>
              <Link to="/dashboard/rfqs" className="text-cyan-400 text-sm hover:text-cyan-300">
                View all →
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs text-white/40 font-medium p-4">RFQ ID</th>
                    <th className="text-left text-xs text-white/40 font-medium p-4">Customer</th>
                    <th className="text-left text-xs text-white/40 font-medium p-4">Status</th>
                    <th className="text-left text-xs text-white/40 font-medium p-4">Due Date</th>
                    <th className="text-right text-xs text-white/40 font-medium p-4">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {mockRFQs.map((rfq) => (
                    <tr key={rfq.id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="p-4">
                        <span className="text-cyan-400 text-sm font-medium">{rfq.id}</span>
                      </td>
                      <td className="p-4 text-sm text-white/80">{rfq.customer}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                          {rfq.status}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-white/60">{rfq.dueDate}</td>
                      <td className="p-4 text-sm text-white/80 text-right font-medium">{rfq.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
